import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { masterFirebaseService } from '../services/firebaseService';
import { DashboardMetrics, OnboardingData } from '../types';
import {
  Building2,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  UserPlus,
  Loader2,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [onboardings, setOnboardings] = useState<OnboardingData[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [m, o] = await Promise.all([
        masterFirebaseService.getDashboardMetrics(),
        masterFirebaseService.getOnboardingsPendentes(),
      ]);
      setMetrics(m);
      setOnboardings(o);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const getOnboardingStatusInfo = (status: string) => {
    const map: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: <Clock className="h-3 w-3" /> },
      aguardando_pagamento: { label: 'Aguardando pgto', color: 'bg-blue-100 text-blue-700', icon: <DollarSign className="h-3 w-3" /> },
      concluido: { label: 'Concluído', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="h-3 w-3" /> },
      abandonado: { label: 'Abandonado', color: 'bg-red-100 text-red-700', icon: <XCircle className="h-3 w-3" /> },
    };
    return map[status] || { label: status, color: 'bg-gray-100 text-gray-600', icon: <Clock className="h-3 w-3" /> };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Visão geral da plataforma SwimmingPool</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/clientes')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 rounded-lg bg-primary-100">
              <Building2 className="h-5 w-5 text-primary-600" />
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{metrics?.totalClientes || 0}</p>
          <p className="text-sm text-gray-500 mt-1">Total de clientes</p>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-green-600 font-medium">{metrics?.clientesAtivos || 0} ativos</span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-500">{metrics?.clientesInativos || 0} inativos</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 rounded-lg bg-green-100">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(metrics?.receitaMensal || 0)}</p>
          <p className="text-sm text-gray-500 mt-1">Receita mensal estimada</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className={`p-2 rounded-lg ${(metrics?.clientesEmAtraso || 0) > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
              <AlertTriangle className={`h-5 w-5 ${(metrics?.clientesEmAtraso || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`} />
            </div>
          </div>
          <p className={`text-3xl font-bold ${(metrics?.clientesEmAtraso || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {metrics?.clientesEmAtraso || 0}
          </p>
          <p className="text-sm text-gray-500 mt-1">Clientes em atraso</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 rounded-lg bg-secondary-100">
              <UserPlus className="h-5 w-5 text-secondary-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{metrics?.onboardingsPendentes || 0}</p>
          <p className="text-sm text-gray-500 mt-1">Onboardings pendentes</p>
        </div>
      </div>

      {onboardings.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Onboardings em andamento</h2>
            <p className="text-sm text-gray-500">Clientes em processo de cadastro</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Nome</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Empresa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Plano</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Etapa</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Última atividade</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {onboardings.slice(0, 10).map((ob, index) => {
                  const statusInfo = getOnboardingStatusInfo(ob.status);
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{ob.nome}</p>
                        <p className="text-xs text-gray-500">{ob.email}</p>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <p className="text-sm text-gray-700">{ob.empresaNome || '-'}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <p className="text-sm text-gray-700">{ob.planoNome || '-'}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-medium text-gray-700">{ob.etapaAtual}/6</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.icon} {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right hidden lg:table-cell">
                        <span className="text-xs text-gray-500">{formatDate(ob.dataUltimaAtividade)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

